import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Field, useField, useFormikContext } from 'formik'
import { MuiTextFieldFormik } from '@user/components/Formik'
import {
  IFilterProps,
  IMultiSelectFilter,
  IOption,
  IPropsWithProvider,
  OptionDependency,
} from '../../typings'
import { resolveOptionLabels } from '../../utils'
import CollapsibleFilter from '../atoms/CollapsibleFilter'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import { MultiSelect } from '@workwave-tidal/core/components'

interface Props extends IFilterProps, IPropsWithProvider {
  dependsOn?: OptionDependency
  hideModeSelect?: boolean
  isOptionEqual?: (optionA: IOption, optionB: IOption) => boolean
  hideCheckBox?: boolean
  renderSummary?: (options: IOption[]) => string
}

export const MultiSelectTidalFilter: FC<Props> = <T,>({
  name,
  label,
  dependsOn,
  component: Component,
  hideModeSelect = false,
  isOptionEqual,
  reset,
  hideCheckBox = true,
  renderSummary,
}) => {
  const path = `filters.${name}`
  const modeField = `${path}.value.mode`
  const optionField = `${path}.value.options`
  const [{ value: filter }] = useField<IMultiSelectFilter<T>>(path)
  const { propertyIsArray } = filter

  const { setFieldValue } = useFormikContext()
  const [dep, setDep] = useState(dependsOn)
  const setValue = useCallback(
    (values: IOption[]) => {
      setFieldValue(optionField, values)
    },
    [optionField, setFieldValue],
  )
  const rOptions = useRef(filter.value.options)
  // update the ref.current when value change
  useEffect(() => {
    rOptions.current = filter.value.options
  }, [filter.value.options])
  // clear selections when dependency value changes
  useEffect(() => {
    if (
      dependsOn !== undefined &&
      dependsOn != dep &&
      dependsOn.length === 0 &&
      dependsOn.length < dep.length
    ) {
      setFieldValue(optionField, [])
      setDep(dependsOn)
    } else {
      if (dependsOn?.length > 0) {
        const filtered = rOptions.current.filter(
          (dependant) =>
            !dependant.parentId ||
            dependsOn.some(
              (dependsOnEntry) =>
                dependant.parentId &&
                dependant.parentId == dependsOnEntry.value,
            ),
        )
        setFieldValue(optionField, filtered ?? [])
      }
    }
  }, [dep, dependsOn, optionField, setFieldValue])

  const getOptionLabel = useCallback((option: IOption) => {
    return option.label
  }, [])

  const getId = useCallback((option: IOption) => {
    return String(option.value)
  }, [])

  const renderSummaryDefault = useCallback((options: IOption[]) => {
    if (options.length === 1) {
      const text = options[0].label

      return text.length > 30 ? text.slice(0, 30) + '..' : text
    } else {
      return `${new Set(options.map((x) => x.value)).size} selected`
    }
  }, [])
  const theme = useTheme()
  return (
    <Component
      fieldName={name}
      dependsOn={dependsOn}
      render={(options, isFetching) =>
        !isFetching && (
          <CollapsibleFilter
            label={label}
            subLabel={(expanded) =>
              !expanded &&
              filter.value.options.length > 0 &&
              `${filter.value.options.length} Selected`
            }
            reset={reset}
          >
            <Grid container spacing={2}>
              {!hideModeSelect && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography color="textSecondary">Match</Typography>
                    <Field
                      name={modeField}
                      component={MuiTextFieldFormik}
                      select
                      size="small"
                      style={{
                        margin: `0 ${theme.spacing(1)}`,
                        minWidth: '80px',
                      }}
                    >
                      {propertyIsArray && <MenuItem value="all">All</MenuItem>}
                      <MenuItem value="any">Any</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </Field>
                    <Typography color="textPrimary">
                      of the following
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <MultiSelect<IOption>
                  // The selection is persisted as a `{ value, label }`
                  // snapshot, so read the label back off the freshly loaded
                  // items — otherwise a renamed entry keeps its old name here
                  // while the dropdown list below shows the new one.
                  value={resolveOptionLabels(filter.value.options, options)}
                  label={label}
                  items={options}
                  onChange={setValue}
                  getLabel={getOptionLabel}
                  getId={getId}
                  inputMode={'summary'}
                  optionMode={'normal'}
                  placeholder={label}
                  hideCheckbox={hideCheckBox}
                  autocompleteProps={{
                    isOptionEqualToValue: isOptionEqual,
                  }}
                  renderSummary={
                    renderSummary ? renderSummary : renderSummaryDefault
                  }
                />
              </Grid>
            </Grid>
          </CollapsibleFilter>
        )
      }
    />
  )
}
